import React from 'react';
import {
  Grid,
  Typography,
  Avatar,
  Box,
  Link as MuiLink,
  Stack,
  Button,
  Chip,
  useMediaQuery,
} from '@mui/material';
import { motion } from 'framer-motion';
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';
import profilePicture from '../assets/ProfilePicture.jpeg';
import CV from '../assets/GuillermoTafoya_CV.pdf';

const heroHighlights = [
  'Harvard Medical School - FNNDSC',
  'MIT Media Lab - MITree',
  'Incoming Software Engineer - Amazon',
];

const impactRows = [
  {
    label: 'Research',
    value:
      'Co-author of a NeuroImage paper on generative normative modeling for fetal brain anomaly detection.',
  },
  {
    label: 'Products',
    value: 'Builds AI-powered tools and full-stack experiences for healthcare, finance, and education.',
  },
  {
    label: 'Community',
    value: 'Leads cross-border innovation programs connecting Mexico with global tech ecosystems.',
  },
];

const MotionBox = motion(Box);
const floatingShapes = [
  {
    sx: { top: { xs: -90, md: -120 }, left: { xs: -40, md: -80 } },
    size: { xs: 220, md: 260 },
    gradient: 'radial-gradient(circle, rgba(56,189,248,0.45) 0%, rgba(12,18,34,0) 70%)',
    delay: 0,
  },
  {
    sx: { bottom: { xs: -110, md: -140 }, left: { xs: -60, md: -100 } },
    size: { xs: 260, md: 320 },
    gradient: 'radial-gradient(circle, rgba(244,114,182,0.35) 0%, rgba(12,18,34,0) 70%)',
    delay: 1.4,
  },
  {
    sx: { top: { xs: 40, md: 60 }, right: { xs: -50, md: -80 } },
    size: { xs: 200, md: 240 },
    gradient: 'radial-gradient(circle, rgba(129,140,248,0.32) 0%, rgba(12,18,34,0) 65%)',
    delay: 0.8,
  },
];

const AboutMe = () => {
  const isMobile = useMediaQuery('(max-width:600px)');
  const chipDelays = [0, 0.35, 0.7];
  const activeShapes = isMobile ? floatingShapes.slice(0, 2) : floatingShapes;

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 0,
          overflow: 'hidden',
        }}
      >
        {activeShapes.map((shape, index) => (
          <MotionBox
            key={index}
            sx={{
              position: 'absolute',
              width: shape.size,
              height: shape.size,
              borderRadius: '50%',
              background: shape.gradient,
              filter: 'blur(32px)',
              ...shape.sx,
            }}
            animate={{
              x: [0, 18, -12, 0],
              y: [0, -22, 16, 0],
              scale: [1, 1.05, 0.96, 1],
            }}
            transition={{
              duration: 18 + index * 3,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: shape.delay,
            }}
            {...shape}
          />
        ))}
      </Box>

      <Grid
        container
        spacing={{ xs: 6, md: 10 }}
        alignItems="center"
        justifyContent="space-between"
        sx={{ position: 'relative', zIndex: 1 }}
      >
        <Grid item xs={12} md={6}>
          <Stack spacing={3}>
            <Typography variant="overline" sx={{ color: 'primary.light', letterSpacing: 2 }}>
              Crafting humane AI experiences
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, lineHeight: 1.15 }}>
              Hi, I&apos;m Guillermo Tafoya.
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: '1.05rem' }}>
              I majored in Computer Science at{' '}
              <MuiLink href="https://tec.mx/en" target="_blank" rel="noopener noreferrer" underline="hover">
                Tecnologico de Monterrey
              </MuiLink>
              , where I bridge cutting-edge research with thoughtful product design. I have experience working
              inside the{' '}
              <MuiLink href="https://www.fnndsc.org/" target="_blank" rel="noopener noreferrer" underline="hover">
                Fetal-Neonatal Neuroimaging &amp; Developmental Science Center
              </MuiLink>{' '}
              at Harvard Medical School{' '}
              <MuiLink
                href="https://research.childrenshospital.org/neuroim/people/student-intern-guillermo-tafoya-milo"
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
              >
                [member spotlight]
              </MuiLink>
              , Infosys, Banorte, Cemex, and the{' '}
              <MuiLink href="https://www.media.mit.edu/" target="_blank" rel="noopener noreferrer" underline="hover">
                MIT Media Lab
              </MuiLink>{' '}
              <MuiLink href="https://sites.mit.edu/tafoya/" target="_blank" rel="noopener noreferrer" underline="hover">
                [MIT site]
              </MuiLink>
              .
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: '1.05rem' }}>
              My focus spans generative modeling for healthcare, inclusive product experiences, and communities
              that empower Latin American builders. I am especially proud of{' '}
              <MuiLink
                href="https://doi.org/10.1016/j.neuroimage.2025.121442"
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
              >
                our upcoming NeuroImage publication
              </MuiLink>{' '}
              on conditional deep generative normative modeling for fetal brain anomaly detection.
            </Typography>

            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Button
                variant="contained"
                color="primary"
                size="large"
                endIcon={<ArrowOutwardRoundedIcon fontSize="small" />}
                href={CV}
                target="_blank"
                rel="noopener noreferrer"
              >
                View CV
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                size="large"
                endIcon={<ArrowOutwardRoundedIcon fontSize="small" />}
                href="https://doi.org/10.1016/j.neuroimage.2025.121442"
                target="_blank"
                rel="noopener noreferrer"
              >
                Read NeuroImage paper
              </Button>
            </Stack>
          </Stack>
        </Grid>

        <Grid item xs={12} md={5}>
          <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
            <MotionBox
              sx={{
                position: 'relative',
                width: { xs: 240, sm: 280 },
                height: { xs: 240, sm: 280 },
                borderRadius: '28px',
                overflow: 'hidden',
                boxShadow: '0 30px 60px rgba(8, 15, 35, 0.55)',
                border: '1px solid rgba(148, 163, 184, 0.25)',
                background: 'linear-gradient(145deg, rgba(30,64,175,0.45), rgba(2,6,23,0.9))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
              }}
              animate={{
                rotate: [0, 3, -2, 0],
                boxShadow: [
                  '0 30px 60px rgba(8, 15, 35, 0.55)',
                  '0 30px 65px rgba(25, 55, 109, 0.6)',
                  '0 28px 55px rgba(8, 15, 35, 0.55)',
                ],
              }}
              transition={{
            duration: 16,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        >
              <Avatar
                alt="Guillermo Tafoya"
                src={profilePicture}
                variant="rounded"
                sx={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '22px',
                }}
              />
            </MotionBox>
            <MotionBox
              sx={{
                position: 'absolute',
                bottom: { xs: -28, sm: -36 },
                right: { xs: 0, sm: 12 },
                width: { xs: 220, sm: 240 },
                background: 'rgba(8, 14, 30, 0.95)',
                borderRadius: 3,
                border: '1px solid rgba(94, 234, 212, 0.25)',
                backdropFilter: 'blur(16px)',
                p: 2.5,
                boxShadow: '0 16px 40px rgba(20, 184, 166, 0.3)',
              }}
              animate={{
                y: [0, -8, 0],
                boxShadow: [
                  '0 16px 40px rgba(20, 184, 166, 0.3)',
                  '0 22px 44px rgba(20, 184, 166, 0.4)',
                  '0 16px 40px rgba(20, 184, 166, 0.3)',
                ],
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            >
              <Typography variant="subtitle2" sx={{ color: 'secondary.light', fontWeight: 600 }}>
                Current focus
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Making it to January.
              </Typography>
            </MotionBox>
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ mt: { xs: 8, md: 10 } }}>
        <Stack direction="row" spacing={1.5} flexWrap="wrap">
          {heroHighlights.map((item, index) => (
            <MotionBox
              key={item}
              whileHover={{ scale: 1.05, y: -4 }}
              animate={{ y: [0, -6, 0] }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: chipDelays[index % chipDelays.length],
              }}
            >
              <Chip
                label={item}
                variant="outlined"
                sx={{
                  color: 'text.secondary',
                  borderColor: 'rgba(148, 163, 184, 0.25)',
                  backgroundColor: 'rgba(15, 23, 42, 0.65)',
                  fontWeight: 500,
                }}
              />
            </MotionBox>
          ))}
        </Stack>

        <Grid container spacing={3} sx={{ mt: { xs: 5, md: 6 } }}>
          {impactRows.map((item) => (
            <Grid item xs={12} md={4} key={item.label}>
              <Box
                sx={{
                  p: 3,
                  borderRadius: 3,
                  background: 'linear-gradient(145deg, rgba(9, 13, 32, 0.92), rgba(12, 20, 42, 0.84))',
                  border: '1px solid rgba(148, 163, 184, 0.12)',
                  height: '100%',
                }}
              >
                <Typography variant="subtitle2" sx={{ color: 'primary.light', mb: 1 }}>
                  {item.label}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {item.value}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default AboutMe;
